/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
/* Import component-specific CSS */
import './AddEditFitnessCertificate.css';
/* Import AddEditPermit.css for file input styling */
// import '../Permit/AddEditPermit.css';
import { useFormik } from 'formik';
import classNames from 'classnames';
import * as Yup from 'yup';
import { handleGetRequest } from '../../services/GetTemplate';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { handlePostRequest } from '../../services/PostTemplate';
import { Dialog } from 'primereact/dialog';
import QRCode from 'qrcode.react';
import { toast } from 'react-toastify';


const AddEditFitnessCertificate = () => {
    const [isViewMode] = useState(false); // Added for consistency with AddEditPermitForm.js
    const [checkboxValues, setCheckboxValues] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [fitnessRequestCategories, setFitnessRequestCategories] = useState([]);
    const [categoryItems, setCategoryItems] = useState({});
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [checklistError, setChecklistError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [qrCodeValue, setQrCodeValue] = useState();
    const [qrDialogVisible, setQrDialogVisible] = useState(false);
    const [actionType, setActionType] = useState(null);

    // File handling state
    const [vehicleImage, setVehicleImage] = useState(null);
    const [vehicleImagePreview, setVehicleImagePreview] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [imageName, setImageName] = useState(null);
    const [imageVisible, setImageVisible] = useState(false);
    const [isLocalFile, setIsLocalFile] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const edit = location?.state?.editable;
    const dispatch = useDispatch();

    // Get all checklist items from all categories
    const allChecklistItems = fitnessRequestCategories.flatMap(category => 
        categoryItems[category.id] ? categoryItems[category.id].map(item => item.id) : []
    );

    const handleCheckAllChange = (checked, categoryId) => {
        const items = categoryItems[categoryId];
        if (!items?.length) return;
        
        if (checked) {
            // Add all item IDs that aren't already selected
            const itemIds = items.map(item => item.id);
            const newValues = [...checkboxValues];
            
            itemIds.forEach(id => {
                if (!newValues.includes(id)) {
                    newValues.push(id);
                }
            });
            
            setCheckboxValues(newValues);
        } else {
            // Remove all item IDs from this category
            const itemIds = items.map(item => item.id);
            setCheckboxValues(checkboxValues.filter(id => !itemIds.includes(id)));
        }
    };

    const isSectionAllChecked = (categoryId) => {
        const items = categoryItems[categoryId];
        if (!items?.length) return false;
        return items.every((item) => checkboxValues.includes(item.id));
    };

    const handleCheckboxChange = (e) => {
        const { value, checked } = e;
        // value should be the item ID (number)
        const itemId = parseInt(value);
        if (checked) {
            setCheckboxValues([...checkboxValues, itemId]);
        } else {
            setCheckboxValues(checkboxValues.filter((val) => val !== itemId));
        }
    };

    const getVehiclesList = async () => {
        const response = await handleGetRequest('/api/fitness/fee', false);
        if (response?.success) {
            setVehicleTypes(response?.data);
        }
    };

    const fetchFitnessRequestCategories = async () => {
        const response = await handleGetRequest('/api/fitness/inspection', false);
        if (response?.success) {
            const categories = response.data;
            setFitnessRequestCategories(categories);
            // After fetching categories, fetch all items for each category
            await fetchAllCategoryItems(categories);
        }
    };

    const fetchCategoryItems = async (categoryId) => {
        const response = await handleGetRequest(`/api/fitness/inspection/${categoryId}`, false);
        if (response?.success) {
            return response.data;
        }
        return [];
    };
    
    const fetchAllCategoryItems = async (categories) => {
        const itemsObj = {};
        
        for (const category of categories) {
            const items = await fetchCategoryItems(category.id);
            itemsObj[category.id] = items;
        }
        
        setCategoryItems(itemsObj);
    };

    useEffect(() => {
        getVehiclesList();
        fetchFitnessRequestCategories();
    }, []);

    // Remove the automatic validation in useEffect since it will be checked at submission time
    // Reset checklistError whenever the checkbox values change
    useEffect(() => {
        setChecklistError(false);
        setSubmitDisabled(false);
    }, [checkboxValues]);

    // Cleanup object URLs when component unmounts
    useEffect(() => {
        return () => {
            // Clean up any object URLs to prevent memory leaks
            if (vehicleImagePreview) {
                URL.revokeObjectURL(vehicleImagePreview);
            }
        };
    }, []);
    
    // Handle form submission with status
    const handleSubmitWithStatus = (status) => {
        setActionType(status);
        
        // If the action is "approved" (accept), we need to validate that all checklist items are selected
        if (status === 'approved') {
            // Get all items from all categories
            const allItems = fitnessRequestCategories.flatMap(category => 
                categoryItems[category.id] ? categoryItems[category.id].map(item => item.id) : []
            );
            
            // Check if all items are selected
            const allSelected = allItems.every(itemId => checkboxValues.includes(itemId)) && allItems.length > 0;
            
            if (!allSelected) {
                // Show error and prevent submission
                setChecklistError(true);
                
                // Scroll to the checklist section to make the error visible
                setTimeout(() => {
                    const checklistElement = document.querySelector('.checklist-section');
                    if (checklistElement) {
                        checklistElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);
                
                return; // Stop form submission
            }
        }
        
        // Proceed with form submission
        formik.handleSubmit();
    };

    const validationSchema = Yup.object({
        customerName: Yup.string()
            .required('Customer Name is required')
            .matches(/^[A-Za-z\s]+$/, 'Name can only contain letters'),
        customerMobile: Yup.string()
            .required('Customer Mobile is required')
            .matches(/^03\d{9}$/, 'Mobile number should be in format 03XXXXXXXXX'),
        customerCNIC: Yup.string()
            .required('Customer CNIC is required')
            .matches(/^\d{5}-\d{7}-\d{1}$/, 'CNIC should be in format XXXXX-XXXXXXX-X'),
        vehicleNo: Yup.string()
            .required('Vehicle Number is required')
            .matches(/^[A-Za-z]{1,3}-\d{1,4}$/, 'Format should be like ABC-123'),
        typeOfVehicle: Yup.string().required('Vehicle Category is required'),
        make: Yup.string().required('Make is required'),
        model: Yup.string().required('Model is required'),
        chassisNo: Yup.string().required('Chassis Number is required'),
        engineNo: Yup.string().required('Engine Number is required'),
        // fee: Yup.number().typeError('Fee must be a number').min(0, 'Fee cannot be negative').required('Fee is required'),
        noOfSeats: Yup.number()
            .typeError('Number of Seats must be a number')
            .min(1, 'At least one seat is required')
            .max(200, 'Number of seats seems to high')
            .required('Number of Seats is required'),
        ownership: Yup.string().required('Ownership is required'),
        uL_KG: Yup.number().typeError('Unladen weight must be a number').min(0, 'Weight must be positive').required('Unladen weight is required'),
        rlW_KG: Yup.number().typeError('Registered Laden Weight must be a number').min(0, 'Weight must be positive').required('Registered Laden Weight is required'),
        faW_KG: Yup.number().typeError('FAW must be a number').min(0, 'Weight must be positive').required('FAW is required'),
        raW_KG: Yup.number().typeError('RAW must be a number').min(0, 'Weight must be positive').required('RAW is required'),
        frontAxle: Yup.string().required('Front Axle info is required'),
        rearAxle: Yup.string().required('Rear Axle info is required'),
        anyOtherAxle: Yup.string().nullable(),
        paymentMethod: Yup.string().required('Payment method is required'),
        totalFitnessFee: Yup.number().typeError('Total Fitness Fee must be a number').min(0, 'Total must be positive').required('Total Fitness Fee is required'),
        vehicleImage: Yup.mixed()
            .test('required', 'Vehicle Image is required', function (value) {
                return value || this.parent.vehicleImageName;
            })
            .test('fileType', 'Only image files are allowed', function (value) {
                if (!value) return true; // Skip validation if no file is uploaded
                return value && value.type && value.type.startsWith('image/');
            })
            .test('fileSize', 'File size must not exceed 5MB', function (value) {
                if (!value) return true; // Skip validation if no file is uploaded
                const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                return value.size <= maxSize;
            })
    });

    const formik = useFormik({
        validationSchema: validationSchema,
        initialValues: {
            customerName: '',
            customerMobile: '',
            customerCNIC: '',
            vehicleNo: '',
            typeOfVehicle: '',
            make: '',
            model: '',
            chassisNo: '',
            engineNo: '',
            // fee: '',
            noOfSeats: '',
            ownership: '',
            uL_KG: '',
            rlW_KG: '',
            faW_KG: '',
            raW_KG: '',
            frontAxle: '',
            rearAxle: '',
            anyOtherAxle: '',
            fitnessFee: '',
            fitnessFine: '',
            totalFitnessFee: '',
            paymentMethod: '',
            vehicleImage: null,
            vehicleImageName: ''
        },
        onSubmit: async (data) => {
            setIsLoading(true);
            try {
                if (edit) {
                    console.log('Editing record:', data);
                } else {
                    // Create FormData for multipart/form-data submission
                    // Updated FormData creation with exact API field names matching the API documentation
                    const formData = new FormData();

                    // Parse all numeric values first to ensure they're passed as actual numbers, not strings
                    const typeOfVehicle = parseInt(data.typeOfVehicle, 10) || 0;
                    const noOfSeats = parseInt(data.noOfSeats, 10) || 0;
                    const ulKg = parseFloat(data.uL_KG) || 0;
                    const rlwKg = parseFloat(data.rlW_KG) || 0;
                    const fawKg = parseFloat(data.faW_KG) || 0;
                    const rawKg = parseFloat(data.raW_KG) || 0;
                    const regRenewalFee = parseInt(data.totalFitnessFee, 10) || 0;
                    const fineAmount = parseInt(data.fitnessFine, 10) || 0;
                    const totalAmount = regRenewalFee + fineAmount;

                    // Add all fields with correct API field names and types
                    formData.append('CustomerName', data.customerName);
                    formData.append('CustomerMobile', data.customerMobile);
                    formData.append('CustomerCNIC', data.customerCNIC);
                    formData.append('VehicleNo', data.vehicleNo);
                    formData.append('TypeOfVehicle', typeOfVehicle);  // Int
                    formData.append('VehicleMake', data.make);
                    formData.append('VehicleModel', data.model);
                    formData.append('ChassisNo', data.chassisNo);
                    formData.append('EngineNo', data.engineNo);
                    formData.append('NoOfSeats', noOfSeats);  // Int
                    formData.append('Ownership', data.ownership);
                    formData.append('UL_KG', ulKg);  // Float
                    formData.append('RLW_KG', rlwKg);  // Float
                    formData.append('FAW_KG', fawKg);  // Float
                    formData.append('RAW_KG', rawKg);  // Float
                    formData.append('FrontAxle', data.frontAxle);
                    formData.append('RearAxle', data.rearAxle);
                    formData.append('AnyOtherAxle', data.anyOtherAxle || '');
                    formData.append('Reg_Renewal_Fee', regRenewalFee);  // Int
                    formData.append('FineAmount', fineAmount);  // Int
                    formData.append('TotalAmount', totalAmount);  // Int
                    formData.append('PaymentMethod', data.paymentMethod);
                    
                    // Add status field based on the action type using numeric values (1 for Approved, 2 for Rejected)
                    formData.append('Status', actionType === 'rejected' ? '2' : '1');

                    // Add the vehicle image file - this field is required
                    if (vehicleImage) {
                        formData.append('VehicleImage', vehicleImage);
                    } else {
                        // Show error if no vehicle image is selected
                        toast.error('Vehicle Image is required');
                        setIsLoading(false);
                        setSubmitDisabled(false);
                        return;
                    }

                    // Add each checked item individually to the formData
                    checkboxValues.forEach(itemId => {
                        formData.append('CheckItem', itemId);
                    });

                    // Send the multipart/form-data request
                    const response = await dispatch(handlePostRequest(formData, '/api/fitness', true, false, '', '', true)); // Set isMultipart param to true

                    if (response?.success === true) {
                        // Skip payment logic if rejected
                        if (actionType !== 'rejected' && formik.values.paymentMethod === '0') {
                            const responseId = response?.data?.requestId;
                            const qrResponse = await handleGetRequest(`/api/fitness/DQR/${responseId}`);
                            if (qrResponse?.success === true) {
                                setQrCodeValue(qrResponse?.data);
                                setQrDialogVisible(true);
                            }
                            else
                            {
                                toast.error(qrResponse?.message || 'Failed to fetch QR code');
                                navigate(-1);

                            }
                        } else {
                            navigate(-1);
                        }
                    }
                    if (response?.success === false) {
                        toast.error(response?.message || 'Failed to submit fitness certificate request');
                    }
                }
            } catch (error) {
                console.error(error);
                setSubmitDisabled(false);
            } finally {
                setSubmitDisabled(false);
                setIsLoading(false);
            }
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    const paymentMethods = [
        {
            label: 'Cash Payment',
            value: '1'
        },
        {
            label: 'Online Payment',
            value: '0'
        }
    ];

    const onHideDialog = () => {
        setQrDialogVisible(false)
        navigate(-1)
    }

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check if file is an image
            if (!file.type.startsWith('image/')) {
                formik.setFieldError('vehicleImage', 'Only image files are allowed');
                formik.setFieldTouched('vehicleImage', true, false);
                return;
            }
            
            // Check file size before setting values
            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
            if (file.size > maxSize) {
                // Show error for large files
                formik.setFieldError('vehicleImage', 'File size must not exceed 5MB');
                formik.setFieldTouched('vehicleImage', true, false);
                return;
            }
            
            formik.setFieldValue('vehicleImage', file);
            formik.setFieldValue('vehicleImageName', file.name);

            formik.setFieldTouched('vehicleImage', true, false);
            formik.setFieldTouched('vehicleImageName', true, false);

            const newErrors = { ...formik.errors };
            delete newErrors['vehicleImage'];
            delete newErrors['vehicleImageName'];
            formik.setErrors(newErrors);

            const previewUrl = URL.createObjectURL(file);

            setVehicleImage(file);
            setVehicleImagePreview(previewUrl);

            setTimeout(() => {
                formik.validateForm().then(() => {
                    if (formik.errors.vehicleImage) {
                        const updatedErrors = { ...formik.errors };
                        delete updatedErrors.vehicleImage;
                        delete updatedErrors.vehicleImageName;
                        formik.setErrors(updatedErrors);
                    }
                });
            }, 0);
        }
    };    
    const showImagePreview = (url, name, isLocal = false) => {
        setImageUrl(url);
        setImageName(name);
        setIsLocalFile(isLocal);
        setImageVisible(true);
    };

    return (
        <div className="p-5 max-w-screen-xl mx-auto bg-white border-round shadow-2">
            <div className="mb-3" style={{ position: 'relative', textAlign: 'center' }}>
                <h4 className="m-0">
                    See Rules 35 of the Pakistan Motor Vehicle Rules 1969
                    <br />
                    <strong>Inspection Report of Motor Vehicle</strong>
                </h4>
                <Button icon="pi pi-times" onClick={() => navigate(-1)} className="p-button-rounded p-button-danger p-button-text p-button-xl" style={{ fontSize: '1rem', position: 'absolute', top: 0, right: 0 }} />
            </div>
            <Dialog
                header={
                    <div className="flex justify-content-between align-items-center px-4 py-2" style={{ height: '80px' }}>
                        <h3 className="m-0">QR Code Payment</h3>
                        <Button icon="pi pi-times" className="p-button-rounded p-button-text" onClick={onHideDialog} />
                    </div>
                }
                visible={qrDialogVisible}
                closable={false}
                onHide={onHideDialog}
                className="image-dialog"
                style={{ width: '90vw', maxWidth: '600px', borderRadius: '12px' }}
                contentStyle={{ padding: '2rem', borderRadius: '0 0 12px 12px' }}
            >
                {qrCodeValue ? (
                    <>
                        <div className="flex flex-column align-items-center text-center">
                            <h4 className="mb-3" style={{ fontWeight: '500', color: '#333' }}>
                                Scan QR Code for Online Payment
                            </h4>

                            <div className="my-3" style={{ background: '#f9f9f9', padding: '1.5rem', borderRadius: '10px' }}>
                                <QRCode value={qrCodeValue?.qrString} size={256} />
                            </div>

                            <p className="text-sm" style={{ color: '#555' }}>
                                Use QR Code or Consumer ID: <strong>{qrCodeValue?.consumerId}</strong> to complete your payment
                            </p>

                            <Button label="Close" onClick={onHideDialog} className="mt-4 p-button-secondary" style={{ borderRadius: '6px', padding: '0.75rem 2rem' }} />
                        </div>
                    </>
                ) : (
                    <p className="text-center text-sm text-red-500">No QR data returned.</p>
                )}
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog
                header={
                    <div className="flex justify-content-between align-items-center px-4 py-2" style={{ height: '80px' }}>
                        <h3 className="m-0">Image Preview: {imageName}</h3>
                        <Button icon="pi pi-times" className="p-button-rounded p-button-text" onClick={() => setImageVisible(false)} />
                    </div>
                }
                visible={imageVisible}
                closable={false}
                onHide={() => setImageVisible(false)}
                className="image-dialog"
                style={{ width: '90vw', maxWidth: '600px', borderRadius: '12px' }}
                contentStyle={{ padding: '1rem', borderRadius: '0 0 12px 12px' }}
            >
                <div className="flex flex-column align-items-center">
                    <div className="my-3" style={{ maxWidth: '100%', overflow: 'auto' }}>
                        {isLocalFile ? (
                            <img src={imageUrl} alt={imageName} style={{ maxWidth: '100%' }} />
                        ) : (
                            <img src={`data:image/jpeg;base64,${imageUrl}`} alt={imageName} style={{ maxWidth: '100%' }} />
                        )}
                    </div>
                    <Button label="Close" onClick={() => setImageVisible(false)} className="mt-3 p-button-secondary" />
                </div>
            </Dialog>

            <form onSubmit={formik.handleSubmit}>
                <div className="grid formgrid p-fluid">
                    <div className="col-12 md:col-4 mb-3">
                        <label>Customer Name</label>
                        <InputText placeholder="Enter customer name" id="customerName" name="customerName" value={formik.values.customerName} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('customerName') }, 'Input__Round ')} />
                        {getFormErrorMessage('customerName')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Customer Mobile</label>
                        <InputText placeholder="03XXXXXXXXX" id="customerMobile" name="customerMobile" value={formik.values.customerMobile} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('customerMobile') }, 'Input__Round ')} />
                        {getFormErrorMessage('customerMobile')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Customer CNIC</label>
                        <InputText
                            placeholder="XXXXX-XXXXXXX-X"
                            id="customerCNIC"
                            name="customerCNIC"
                            value={formik.values.customerCNIC}
                            onChange={(e) => {
                                // Remove all non-digit characters
                                const digits = e.target.value.replace(/[^\d]/g, '');

                                // Format as XXXXX-XXXXXXX-X
                                let formattedValue = '';
                                if (digits.length <= 5) {
                                    formattedValue = digits;
                                } else if (digits.length <= 12) {
                                    formattedValue = `${digits.substring(0, 5)}-${digits.substring(5)}`;
                                } else {
                                    formattedValue = `${digits.substring(0, 5)}-${digits.substring(5, 12)}-${digits.substring(12, 13)}`;
                                }

                                // Update formik value
                                formik.setFieldValue('customerCNIC', formattedValue);
                            }}
                            className={classNames({ 'p-invalid': isFormFieldValid('customerCNIC') }, 'Input__Round ')}
                        />
                        {getFormErrorMessage('customerCNIC')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Vehicle No.</label>
                        <InputText placeholder="ABC-123" id="vehicleNo" name="vehicleNo" value={formik.values.vehicleNo} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('vehicleNo') }, 'Input__Round ')} />
                        {getFormErrorMessage('vehicleNo')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Vehicle Category</label>
                        <Dropdown
                            options={vehicleTypes}
                            optionLabel="categoryName"
                            optionValue="id"
                            placeholder="Please select vehicle category"
                            id="typeOfVehicle"
                            name="typeOfVehicle"
                            value={formik.values.typeOfVehicle}
                            onChange={(e) => {
                                formik.handleChange(e);
                                const selectedVehicle = vehicleTypes.find((vehicle) => vehicle.id === e.value);
                                if (selectedVehicle) {
                                    formik.setFieldValue('totalFitnessFee', selectedVehicle.normalFee);
                                } else {
                                    formik.setFieldValue('totalFitnessFee', '');
                                }
                            }}
                            className={classNames({ 'p-invalid': isFormFieldValid('typeOfVehicle') }, 'Dropdown__Round ')}
                        />
                        {getFormErrorMessage('typeOfVehicle')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Make</label>
                        <InputText placeholder="Enter make " id="make" name="make" value={formik.values.make} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('make') }, 'Input__Round ')} />
                        {getFormErrorMessage('make')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Model</label>
                        <InputText placeholder="Enter model" id="model" name="model" value={formik.values.model} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('model') }, 'Input__Round ')} />
                        {getFormErrorMessage('model')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Chassis No.</label>
                        <InputText placeholder="Enter chassis no" id="chassisNo" name="chassisNo" value={formik.values.chassisNo} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('chassisNo') }, 'Input__Round ')} />
                        {getFormErrorMessage('chassisNo')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Engine No.</label>
                        <InputText placeholder="Enter engine no" id="engineNo" name="engineNo" value={formik.values.engineNo} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('engineNo') }, 'Input__Round ')} />
                        {getFormErrorMessage('engineNo')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>No. of Seats</label>
                        <InputText placeholder="Enter no. of seats" id="noOfSeats" name="noOfSeats" value={formik.values.noOfSeats} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('noOfSeats') }, 'Input__Round ')} />
                        {getFormErrorMessage('noOfSeats')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Ownership</label>
                        <InputText placeholder="Enter ownership" id="ownership" name="ownership" value={formik.values.ownership} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('ownership') }, 'Input__Round ')} />
                        {getFormErrorMessage('ownership')}
                    </div>

                    <div className="col-12 md:col-4 mb-3">
                        <div className="flex flex-column">
                            <div className="flex justify-content-between">
                                <label htmlFor="vehicleImage" className={classNames({ 'p-error': isFormFieldValid('vehicleImage') }, 'Label__Text')}>
                                    Vehicle Image <span className="Label__Required">*</span>
                                </label>
                                {(vehicleImage || formik.values.vehicleImageName) && (
                                    <i
                                        className="pi pi-eye cursor-pointer"
                                        onClick={() => {
                                            if (vehicleImage) {
                                                // For local preview
                                                showImagePreview(vehicleImagePreview, 'Vehicle Image', true);
                                            } else if (formik.values.vehicleImageName) {
                                                // For edit mode with existing file
                                                showImagePreview(formik.values.vehicleImage, 'Vehicle Image');
                                            }
                                        }}
                                    ></i>
                                )}
                            </div>

                            <div className="file-input-wrapper">
                                <InputText
                                    type="file"
                                    accept="image/*"
                                    id="vehicleImage"
                                    onBlur={formik.handleBlur}
                                    name="vehicleImage"
                                    onChange={(e) => {
                                        handleFileChange(e);
                                    }}
                                    className={`hidden-file-input ${classNames({ 'p-invalid': isFormFieldValid('vehicleImage') })}`}
                                />
                                <div
                                    className={`custom-file-input Input__Round ${classNames({ 'p-invalid': isFormFieldValid('vehicleImage') })}`}
                                    onClick={() => !isViewMode && document.getElementById('vehicleImage').click()}
                                >
                                    <span className="file-name">
                                        {formik.values.vehicleImageName ?
                                            formik.values.vehicleImageName :
                                            (vehicleImage ? vehicleImage.name : "Choose file...")}
                                    </span>
                                    {!isViewMode && <span className="file-button">Browse</span>}
                                </div>
                            </div>

                            {getFormErrorMessage('vehicleImage')}
                        </div>
                    </div>
                </div>

                <Divider />
                <h5 className="text-center">Weighment Particulars Types</h5>
                <div className="grid formgrid p-fluid">
                    <div className="col-12 md:col-4 mb-3">
                        <label>U/L (kg)</label>
                        <InputText placeholder="Enter U/L (kg)" id="uL_KG" name="uL_KG" value={formik.values.uL_KG} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('uL_KG') }, 'Input__Round ')} />
                        {getFormErrorMessage('uL_KG')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>R.L.W (kg)</label>
                        <InputText placeholder="Enter R.L.W (kg)" id="rlW_KG" name="rlW_KG" value={formik.values.rlW_KG} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('rlW_KG') }, 'Input__Round ')} />
                        {getFormErrorMessage('rlW_KG')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>F.A.W (kg)</label>
                        <InputText placeholder="Enter F.A.W (kg)" id="faW_KG" name="faW_KG" value={formik.values.faW_KG} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('faW_KG') }, 'Input__Round ')} />
                        {getFormErrorMessage('faW_KG')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>R.A.W (kg)</label>
                        <InputText placeholder="Enter R.A.W (kg)" id="raW_KG" name="raW_KG" value={formik.values.raW_KG} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('raW_KG') }, 'Input__Round ')} />
                        {getFormErrorMessage('raW_KG')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Front Axle</label>
                        <InputText placeholder="Enter Front Axle" id="frontAxle" name="frontAxle" value={formik.values.frontAxle} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('frontAxle') }, 'Input__Round ')} />
                        {getFormErrorMessage('frontAxle')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Rear Axle</label>
                        <InputText placeholder="Enter Rear Axle" id="rearAxle" name="rearAxle" value={formik.values.rearAxle} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('rearAxle') }, 'Input__Round ')} />
                        {getFormErrorMessage('rearAxle')}
                    </div>
                    <div className="col-12 md:col-4 mb-3">
                        <label>Any Other Axle</label>
                        <InputText
                            placeholder="Enter any other axle"
                            id="anyOtherAxle"
                            name="anyOtherAxle"
                            value={formik.values.anyOtherAxle}
                            onChange={formik.handleChange}
                            className={classNames({ 'p-invalid': isFormFieldValid('anyOtherAxle') }, 'Input__Round ')}
                        />
                        {getFormErrorMessage('anyOtherAxle')}
                    </div>
                </div>

                <Divider />
                <h5 className="mb-2">Checklist</h5>

                {fitnessRequestCategories.map((category, categoryIndex) => (
                    <div key={category.id} className="mb-3 border p-3 border-round">
                        <div className="flex justify-content-between align-items-center mb-2">
                            <strong>{categoryIndex + 1}. {category.categoryName}</strong>
                            <div className="flex align-items-center">
                                <Checkbox 
                                    inputId={`category-${category.id}-checkAll`} 
                                    onChange={(e) => handleCheckAllChange(e.checked, category.id)} 
                                    checked={isSectionAllChecked(category.id)} 
                                />
                                <label htmlFor={`category-${category.id}-checkAll`} className="ml-2">
                                    Check All
                                </label>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-2">
                            {categoryItems[category.id]?.map((item, index) => (
                                <div key={item.id} className="flex align-items-center">
                                    <Checkbox 
                                        inputId={`category-${category.id}-item-${item.id}`} 
                                        value={item.id.toString()} 
                                        onChange={handleCheckboxChange} 
                                        checked={checkboxValues.includes(item.id)} 
                                    />
                                    <label htmlFor={`category-${category.id}-item-${item.id}`} className="ml-2">
                                        {item.itemName}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                <div className="checklist-section">
                    {checklistError && <p className="p-error text-center">All inspection items must be selected before accepting the application.</p>}
                </div>

                <Divider />
                <h3 className="section-title">Payment Method</h3>
                <div className="p-fluid p-formgrid grid mb-5 col-12 md:col-12">
                    <div className="p-fluid p-formgrid grid col-12 md:col-4">
                        <div className="p-field col-12 md:col-12 pt-3">
                            <div className="p-field">
                                <label htmlFor="paymentMethod" className={classNames({ 'p-erro': isFormFieldValid('paymentMethod') }, 'Label__Text')}>
                                    Payment Method<span className="Label__Required">*</span>
                                </label>
                                <Dropdown
                                    options={paymentMethods}
                                    optionValue="value"
                                    optionLabel="label"
                                    id="paymentMethod"
                                    placeholder="Please select payment method"
                                    onBlur={formik.handleBlur}
                                    name="paymentMethod"
                                    value={formik.values.paymentMethod || ''}
                                    onChange={formik.handleChange}
                                    className={classNames({ 'p-invalid': isFormFieldValid('paymentMethod') }, 'Dropdown__Round ')}
                                />
                                {getFormErrorMessage('paymentMethod')}
                            </div>
                        </div>
                    </div>
                </div>
                <Divider />
                <h5>Fee Details</h5>
                <div className="grid formgrid p-fluid">
                    {edit && (
                        <>
                            <div className="col-12 md:col-4 mb-3">
                                <label>Fitness Fee</label>
                                <InputText
                                    placeholder="Enter fitness fee"
                                    id="fitnessFee"
                                    name="fitnessFee"
                                    value={formik.values.fitnessFee}
                                    onChange={formik.handleChange}
                                    className={classNames({ 'p-invalid': isFormFieldValid('fitnessFee') }, 'Input__Round ')}
                                />
                                {getFormErrorMessage('fitnessFee')}
                            </div>
                            <div className="col-12 md:col-4 mb-3">
                                <label>Fitness Fine</label>
                                <InputText
                                    placeholder="0"
                                    id="fitnessFine"
                                    name="fitnessFine"
                                    value={formik.values.fitnessFine}
                                    onChange={formik.handleChange}
                                    className={classNames({ 'p-invalid': isFormFieldValid('fitnessFine') }, 'Input__Round ')}
                                />
                                {getFormErrorMessage('fitnessFine')}
                            </div>
                        </>
                    )}

                    <div className="col-12 md:col-4 mb-3">
                        <label>Total Fitness Fee</label>
                        <InputText
                            placeholder="0"
                            id="totalFitnessFee"
                            name="totalFitnessFee"
                            value={formik.values.totalFitnessFee}
                            onChange={formik.handleChange}
                            className={classNames({ 'p-invalid': isFormFieldValid('totalFitnessFee') }, 'Input__Round ')}
                            disabled
                        />
                        {getFormErrorMessage('totalFitnessFee')}
                    </div>
                </div>

                <div className="flex justify-content-center gap-3 mt-4">
                    <Button 
                        label="Cancel" 
                        className={`fitness-btn fitness-btn-cancel`}
                        type="button" 
                        onClick={() => navigate(-1)} 
                    />
                    <Button 
                        label="Reject" 
                        icon="pi pi-times"
                        loading={isLoading && actionType === 'rejected'} 
                        disabled={submitDisabled || isLoading} 
                        className={`fitness-btn fitness-btn-reject ${(submitDisabled || isLoading) ? 'fitness-btn-disabled' : ''}`}
                        type="button"
                        onClick={() => handleSubmitWithStatus('rejected')} 
                    />
                    <Button 
                        label="Approve" 
                        icon="pi pi-check"
                        loading={isLoading && actionType === 'approved'} 
                        disabled={submitDisabled || isLoading} 
                        className={`fitness-btn fitness-btn-approve ${(submitDisabled || isLoading) ? 'fitness-btn-disabled' : ''}`}
                        type="button"
                        onClick={() => handleSubmitWithStatus('approved')} 
                    />
                </div>
            </form>
        </div>
    );
};

export default AddEditFitnessCertificate;
